import { useState, useMemo, useEffect } from 'react';
import dynamic from 'next/dynamic';
import type { ComponentsSubscriptionCart } from 'queries/graphql';
import { Elements } from '@stripe/react-stripe-js';

import { type SalesFunnelData, useFunnelData } from 'utils/funnel-data-context';
import { useCart } from 'utils/cart-context';
import { trackViewedProduct } from 'utils/klaviyo';
import { convertPrice } from 'utils/price';
import formatCurrency from 'utils/format-currency';
import useLocalCurrency from 'utils/useLocalCurrency';
import { useHeadingFontOverrides, useTextFontOverrides } from 'utils/font-override';
import { buildCssVar } from 'utils/style-override';
import * as gtm from 'utils/gtm';
import * as rudder from 'utils/rudderstack';
import { generateSkioUrlsForOffer } from 'utils/sellingPlan';
import { useOnCheckoutClick } from 'utils/checkout-link';

import SubscriptionOption from './components/SubscriptionOption';
import { getStripe } from '../../utils/stripe-context';

const StickyBuyButton = dynamic(() => import('./components/StickyBuyButton'), { ssr: false });

import styles from './styles.module.scss';

export default function SubscriptionCart({
  sectionId,
  priceDisplayMode,
  showOfferName,
  showUnits,
  showPricePerUnit,
  showOfferPrice,
  labelUnitsPerOffer,
  labelCostPerUnit,
  labelQuantityUnit,
  subscriptionFrequencyLabel,
  ctaText,
  ctaButtonText,
  style,
  subscriptionCartOffers,
  bulletList,
}: ComponentsSubscriptionCart) {
  const { offersConfig, store } = useFunnelData<SalesFunnelData>();
  const { updateCartItem } = useCart();
  const { symbol: currencySymbol, rate: localFxRate } = useLocalCurrency();
  const [selectedOfferId, setSelectedOfferId] = useState<number | null>(null);
  const [priceOption, setPriceOption] = useState<'subscription' | 'onetime'>('subscription');
  const [, setShouldShowActivityIndicator] = useState(false);

  const getCheckoutClickHandler = useOnCheckoutClick({
    setShouldShowActivityIndicator,
    shouldTrackSelectItem: true,
  });

  const headingFontOverrides = useHeadingFontOverrides(style?.fontFamily);
  const textFontOverrides = useTextFontOverrides(style?.fontFamily);

  const offerPairs = useMemo(() => {
    if (!subscriptionCartOffers) return new Map();

    const pairs = new Map();

    subscriptionCartOffers.forEach(offer => {
      const rawOffer = offer.salesPageOffer?.offer;
      if (!rawOffer) return;

      const pairKey = offer.sortOrder || 0;

      if (!pairs.has(pairKey)) {
        pairs.set(pairKey, { subscription: null, onetime: null });
      }

      const pair = pairs.get(pairKey);
      if (rawOffer.isSubscription) {
        pair.subscription = offer;
      } else {
        pair.onetime = offer;
      }
    });

    return pairs;
  }, [subscriptionCartOffers]);

  const sortedOffers = useMemo(() => {
    if (!subscriptionCartOffers) return [];

    return [...subscriptionCartOffers]
      .filter(function (offer) {
        const rawOffer = offer.salesPageOffer?.offer;
        if (!rawOffer) return false;

        if (priceOption === 'subscription') {
          return rawOffer.isSubscription === true;
        } else {
          return rawOffer.isSubscription === false;
        }
      })
      .sort(function (a, b) {
        return (a.sortOrder || 0) - (b.sortOrder || 0);
      });
  }, [subscriptionCartOffers, priceOption]);

  useEffect(() => {
    if (!selectedOfferId || offerPairs.size === 0) return;

    const currentOffer = subscriptionCartOffers?.find(offer => offer.id === selectedOfferId);
    if (!currentOffer) return;

    const pairKey = currentOffer.sortOrder || 0;
    const pair = offerPairs.get(pairKey);

    if (pair) {
      let pairedOffer = priceOption === 'subscription' ? pair.subscription : pair.onetime;

      // Fallback: if no paired offer, find first available offer of that type
      if (!pairedOffer) {
        pairedOffer = subscriptionCartOffers?.find(offer => {
          const rawOffer = offer.salesPageOffer?.offer;
          if (!rawOffer) return false;

          if (priceOption === 'subscription') {
            return rawOffer.isSubscription === true;
          } else {
            return rawOffer.isSubscription === false;
          }
        });
      }

      if (pairedOffer && pairedOffer.id !== selectedOfferId) {
        setSelectedOfferId(pairedOffer.id);

        const rawOffer = pairedOffer.salesPageOffer?.offer;
        if (rawOffer?.hashedId) {
          const offerConfig = offersConfig.find(o => o.id === rawOffer.hashedId);
          if (offerConfig) {
            updateCartItem({ offerId: offerConfig.id, quantity: 1 });
            trackViewedProduct(rawOffer);
            gtm.onSelectItem(offerConfig);
            rudder.onSelectItem(offerConfig);
          }
        }
      }
    }
  }, [
    priceOption,
    selectedOfferId,
    offerPairs,
    subscriptionCartOffers,
    offersConfig,
    updateCartItem,
  ]);

  useEffect(() => {
    if (sortedOffers.length > 0 && !selectedOfferId) {
      const firstOffer = sortedOffers[0];
      setSelectedOfferId(firstOffer.id);

      const offer = firstOffer.salesPageOffer?.offer;
      if (offer?.hashedId) {
        const offerConfig = offersConfig.find(function (o) {
          return o.id === offer.hashedId;
        });
        if (offerConfig) {
          updateCartItem({ offerId: offerConfig.id, quantity: 1 });
          trackViewedProduct(offer);
        }
      }
    }
  }, [sortedOffers, selectedOfferId, offersConfig, updateCartItem]);

  const skioURL = useMemo(() => {
    if (!subscriptionCartOffers || subscriptionCartOffers.length === 0) return null;

    let subscriptionOffer;

    if (selectedOfferId) {
      const currentOffer = subscriptionCartOffers.find(offer => offer.id === selectedOfferId);
      if (currentOffer?.salesPageOffer?.offer?.isSubscription) {
        subscriptionOffer = currentOffer;
      } else {
        const pairKey = currentOffer?.sortOrder || 0;
        const pair = offerPairs.get(pairKey);
        subscriptionOffer = pair?.subscription;
      }
    }

    if (!subscriptionOffer) {
      subscriptionOffer = subscriptionCartOffers.find(
        offer => offer.salesPageOffer?.offer?.isSubscription
      );
    }

    const rawOffer = subscriptionOffer?.salesPageOffer?.offer;
    if (!rawOffer?.isSubscription) return null;

    try {
      const skioUrls = generateSkioUrlsForOffer(rawOffer);
      return skioUrls.length > 0 ? skioUrls[0].skioUrl : null;
    } catch (error) {
      console.error('Error generating Skio URL:', error);
      return null;
    }
  }, [subscriptionCartOffers, selectedOfferId, offerPairs]);

  const selectedOffer = useMemo(() => {
    return sortedOffers.find(function (offer) {
      return offer.id === selectedOfferId;
    });
  }, [sortedOffers, selectedOfferId]);

  const subscriptionPrice = useMemo(() => {
    if (!subscriptionCartOffers || !selectedOfferId || offerPairs.size === 0) return 0;

    const currentOffer = subscriptionCartOffers.find(offer => offer.id === selectedOfferId);
    if (!currentOffer) return 0;

    const pairKey = currentOffer.sortOrder || 0;
    const pair = offerPairs.get(pairKey);
    let subscriptionOffer = pair?.subscription;

    if (!subscriptionOffer) {
      subscriptionOffer = subscriptionCartOffers.find(
        offer => offer.salesPageOffer?.offer?.isSubscription === true
      );
    }

    if (!subscriptionOffer?.salesPageOffer?.offer) return 0;
    return convertPrice(subscriptionOffer.salesPageOffer.offer.price, localFxRate);
  }, [selectedOfferId, offerPairs, subscriptionCartOffers, localFxRate]);

  const oneTimePrice = useMemo(() => {
    if (!subscriptionCartOffers || !selectedOfferId || offerPairs.size === 0) return 0;

    const currentOffer = subscriptionCartOffers.find(offer => offer.id === selectedOfferId);
    if (!currentOffer) return 0;

    const pairKey = currentOffer.sortOrder || 0;
    const pair = offerPairs.get(pairKey);
    let onetimeOffer = pair?.onetime;

    if (!onetimeOffer) {
      onetimeOffer = subscriptionCartOffers.find(
        offer => offer.salesPageOffer?.offer?.isSubscription === false
      );
    }

    if (!onetimeOffer?.salesPageOffer?.offer) return 0;
    return convertPrice(onetimeOffer.salesPageOffer.offer.price, localFxRate);
  }, [selectedOfferId, offerPairs, subscriptionCartOffers, localFxRate]);

  const compareAtPrice = useMemo(() => {
    if (!subscriptionCartOffers || !selectedOfferId || offerPairs.size === 0) return null;

    const currentOffer = subscriptionCartOffers.find(offer => offer.id === selectedOfferId);
    if (!currentOffer) return null;

    const pairKey = currentOffer.sortOrder || 0;
    const pair = offerPairs.get(pairKey);
    let subscriptionOffer = pair?.subscription;

    if (!subscriptionOffer) {
      subscriptionOffer = subscriptionCartOffers.find(
        offer => offer.salesPageOffer?.offer?.isSubscription === true
      );
    }

    if (!subscriptionOffer?.salesPageOffer?.offer?.compareAtPrice) return null;
    return convertPrice(subscriptionOffer.salesPageOffer.offer.compareAtPrice, localFxRate);
  }, [selectedOfferId, offerPairs, subscriptionCartOffers, localFxRate]);

  const oneTimeCompareAtPrice = useMemo(() => {
    if (!subscriptionCartOffers || !selectedOfferId || offerPairs.size === 0) return null;

    const currentOffer = subscriptionCartOffers.find(offer => offer.id === selectedOfferId);
    if (!currentOffer) return null;

    const pairKey = currentOffer.sortOrder || 0;
    const pair = offerPairs.get(pairKey);
    let onetimeOffer = pair?.onetime;

    if (!onetimeOffer) {
      onetimeOffer = subscriptionCartOffers.find(
        offer => offer.salesPageOffer?.offer?.isSubscription === false
      );
    }

    if (!onetimeOffer?.salesPageOffer?.offer?.compareAtPrice) return null;
    return convertPrice(onetimeOffer.salesPageOffer.offer.compareAtPrice, localFxRate);
  }, [selectedOfferId, offerPairs, subscriptionCartOffers, localFxRate]);

  const currentPrice = useMemo(() => {
    return priceOption === 'subscription' ? subscriptionPrice : oneTimePrice;
  }, [priceOption, subscriptionPrice, oneTimePrice]);

  const handleOneTimeCheckout = () => {
    if (!selectedOffer?.salesPageOffer?.offer) return;

    const offer = selectedOffer.salesPageOffer.offer;
    const offerConfig = offersConfig.find(function (o) {
      return o.id === offer.hashedId;
    });

    if (offerConfig) {
      updateCartItem({ offerId: offerConfig.id, quantity: 1 });
      const cartItems = [{ offerId: offerConfig.id, quantity: 1 }];
      getCheckoutClickHandler(cartItems)(null);
    }
  };

  if (sortedOffers.length === 0) {
    return null;
  }

  return (
    <Elements stripe={getStripe(store?.chargeType, store?.connectedStripeAccountId)}>
      <div id={sectionId} className={`root ${styles.container}`} data-cy="subscription-cart">
        <style jsx>{`
          .root {
            ${buildCssVar('--bg-color', style?.backgroundColor, '#ffffff')}
            ${buildCssVar('--border-color', style?.borderColor, '#e5e7eb')}
            ${buildCssVar('--font-color', style?.fontColor, '#111827')}
            ${buildCssVar('--font-size', style?.fontSize, '16px')}
            ${buildCssVar('--cta-button-color', style?.ctaButtonColor, '#10b981')}
            ${headingFontOverrides ?? ''}
            ${textFontOverrides ?? ''}
          }
        `}</style>

        <div className={styles.offerSection}>
          <h2 className={styles.sectionTitle}>CHOOSE OPTION:</h2>
          <div className={styles.optionsWrapper}>
            {sortedOffers.map(function (offer, index) {
              return (
                <SubscriptionOption
                  key={offer.id}
                  offer={offer}
                  isSelected={selectedOfferId === offer.id}
                  onSelect={function () {
                    return handleOfferSelect(offer.id);
                  }}
                  showOfferName={showOfferName}
                  labelUnitsPerOffer={labelUnitsPerOffer}
                  labelCostPerUnit={labelCostPerUnit}
                  labelQuantityUnit={labelQuantityUnit}
                  showUnits={showUnits}
                  showPricePerUnit={showPricePerUnit}
                  showOfferPrice={showOfferPrice}
                  priceDisplayMode={priceDisplayMode}
                  currencySymbol={currencySymbol}
                  localFxRate={localFxRate}
                  isMostPopular={index === 0}
                  isBestValue={index === 1}
                />
              );
            })}
          </div>
        </div>

        <div className={styles.priceSection}>
          <h2 className={styles.sectionTitle}>CHOOSE PRICE:</h2>

          <div className={styles.priceOptions}>
            <label
              className={`${styles.priceOption} ${
                priceOption === 'subscription' ? styles.selected : ''
              }`}
            >
              <input
                type="radio"
                name="priceOption"
                value="subscription"
                checked={priceOption === 'subscription'}
                onChange={function () {
                  return setPriceOption('subscription');
                }}
                className={styles.radioInput}
              />
              <div className={styles.optionContent}>
                <div className={styles.optionHeader}>
                  <span className={styles.optionTitle}>Subscribe & Save</span>
                  <div className={styles.priceInfo}>
                    <div className={styles.price}>
                      <span>{formatCurrency(subscriptionPrice, false, currencySymbol)}</span>
                      {shouldShowFrequency('subscription') && (
                        <span className={styles.frequency}>
                          {' '}
                          / {getFrequencyLabel('subscription')}
                        </span>
                      )}
                    </div>
                    {compareAtPrice && (
                      <span className={styles.comparePrice}>
                        {formatCurrency(compareAtPrice, false, currencySymbol)}
                      </span>
                    )}
                  </div>
                </div>
                <ul className={styles.benefits}>
                  {bulletList?.bullets?.map(function (bulletItem, index) {
                    return <li key={index}>{bulletItem.bullet.text}</li>;
                  }) || (
                    <>
                      <li>Delivered Every 30 Days</li>
                      <li>Easily Edit, Skip or Cancel Anytime</li>
                      <li>Free Express Shipping</li>
                      <li>30 Day Money Back Guarantee</li>
                      <li>Exclusive Subscriber Benefits</li>
                    </>
                  )}
                </ul>
              </div>
            </label>

            <div className={styles.divider}>OR</div>

            <label
              className={`${styles.priceOption} ${
                priceOption === 'onetime' ? styles.selected : ''
              } ${styles.oneTimePurchase}`}
            >
              <input
                type="radio"
                name="priceOption"
                value="onetime"
                checked={priceOption === 'onetime'}
                onChange={function () {
                  return setPriceOption('onetime');
                }}
                className={styles.radioInput}
              />
              <div className={styles.optionContent}>
                <div className={styles.optionHeader}>
                  <span className={styles.optionTitle}>One-Time Purchase</span>
                  <div className={styles.priceInfo}>
                    <div className={styles.price}>
                      <span>{formatCurrency(oneTimePrice, false, currencySymbol)}</span>
                      {shouldShowFrequency('onetime') && (
                        <span className={styles.frequency}> / {getFrequencyLabel('onetime')}</span>
                      )}
                    </div>
                    {oneTimeCompareAtPrice && (
                      <span className={styles.comparePrice}>
                        {formatCurrency(oneTimeCompareAtPrice, false, currencySymbol)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </label>
          </div>
        </div>

        <StickyBuyButton
          priceOption={priceOption}
          selectedPrice={currentPrice}
          currencySymbol={currencySymbol}
          onCheckout={handleOneTimeCheckout}
          skioURL={skioURL}
          cartLineItem={selectedOffer?.salesPageOffer?.offer}
          ctaText={ctaText}
          ctaButtonText={ctaButtonText}
          ctaButtonColor={style?.ctaButtonColor}
        />
      </div>
    </Elements>
  );

  function handleOfferSelect(offerId: number) {
    setSelectedOfferId(offerId);
    const offer = sortedOffers.find(function (o) {
      return o.id === offerId;
    });

    if (offer?.salesPageOffer?.offer?.hashedId) {
      const offerConfig = offersConfig.find(function (o) {
        return o.id === offer.salesPageOffer.offer.hashedId;
      });
      if (offerConfig) {
        updateCartItem({ offerId: offerConfig.id, quantity: 1 });
        gtm.onSelectItem(offerConfig);
        rudder.onSelectItem(offerConfig);
      }
    }
  }

  function getFrequencyLabel(pricingType: 'subscription' | 'onetime') {
    if (priceDisplayMode === 'total') {
      if (pricingType === 'subscription') {
        return subscriptionFrequencyLabel || 'Month';
      } else {
        return null;
      }
    } else {
      if (pricingType === 'subscription') {
        return labelQuantityUnit || 'Bag';
      } else {
        return labelQuantityUnit || 'Bag';
      }
    }
  }

  function shouldShowFrequency(pricingType: 'subscription' | 'onetime') {
    const label = getFrequencyLabel(pricingType);
    return label !== null;
  }
}
